// @flow

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Accordion } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import GrossRentChangeDrawerSummary from './GrossRentChangeDrawerSummary';
import GrossRentChangeFloorPlanTable from './GrossRentChangeFloorPlanTable/index';
import type { GrossRentChangeDrawerProps } from './types';

const accordionSx = {
  backgroundColor: 'background.paper',
  border: 'none',
  // MUI Accordion still uses height:1px + background (disappears at some zooms).
  // Divider was fixed with `thin`: https://github.com/mui/material-ui/issues/14815
  borderBottomWidth: 'thin',
  borderBottomStyle: 'solid',
  borderColor: 'divider',
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: 0,
  },
  '& .MuiAccordionSummary-root': {
    borderTop: 'none',
  },
};

const staticAccordionProps = {
  square: true,
  elevation: 0,
  disableGutters: true,
};

const accordionSummaryProps = {
  expandIcon: false,
  sx: {
    px: 2,
    py: 1.5,
    minHeight: 'unset',
    borderTop: 'none',
    '&.Mui-expanded': {
      minHeight: 'unset',
    },
    '& .MuiAccordionSummary-content': {
      margin: 0,
    },
    '&.Mui-expanded .MuiAccordionSummary-content': {
      margin: 0,
    },
  },
};

const accordionDetailsProps = {
  sx: {
    px: 1.5,
    pt: 1,
    pb: 1.5,
    display: 'block',
  },
};

const GrossRentChangeDrawer = ({
  grossRentChange,
  onEditChange,
}: GrossRentChangeDrawerProps) => {
  const [expanded, setExpanded] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const {
    effectiveDate,
    anticipatedVoucherDate,
    floorPlans,
    isLocked = false,
    hasCompletedCertifications = false,
  } = grossRentChange;

  // useCallback so the memoized summary doesn't re-render on every drawer render.
  const handleEdit = useCallback(() => {
    if (onEditChange) {
      onEditChange(grossRentChange);
    }
  }, [onEditChange, grossRentChange]);

  const handleChange = useCallback((_event, isExpanded) => {
    if (isExpanded) {
      setShowTable(true);
    }
    setExpanded(isExpanded);
  }, []);

  const handleDetailsTransitionExited = useCallback(() => {
    if (!expandedRef.current) {
      setShowTable(false);
    }
  }, []);

  const accordionProps = useMemo(
    () => ({
      ...staticAccordionProps,
      TransitionProps: {
        onExited: handleDetailsTransitionExited,
      },
    }),
    [handleDetailsTransitionExited],
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      sx={accordionSx}
      AccordionProps={accordionProps}
      AccordionSummaryProps={accordionSummaryProps}
      AccordionDetailsProps={accordionDetailsProps}
      summary={
        <GrossRentChangeDrawerSummary
          effectiveDate={effectiveDate}
          anticipatedVoucherDate={anticipatedVoucherDate}
          expanded={expanded}
          isLocked={isLocked}
          hasCompletedCertifications={hasCompletedCertifications}
          onEdit={handleEdit}
        />
      }
    >
      {showTable ? (
        <GrossRentChangeFloorPlanTable
          effectiveDate={effectiveDate}
          floorPlans={floorPlans}
        />
      ) : null}
    </Accordion>
  );
};

export default GrossRentChangeDrawer;
